import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import BrandBadge from "../components/BrandBadge";
import BenefitsRow from "../components/BenefitsRow";
import SectionHeading from "../components/SectionHeading";
import { useCart } from "../context/CartContext";
import { PRODUCT, formatPrice } from "../data/catalog";
import { PRODUCT_BADGES, SCIENCE_STATS, INGREDIENTS } from "../data/content";

const Home = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-40 w-[700px] h-[700px] soft-orb" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-12 grid lg:grid-cols-2 gap-12 items-center relative">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-royal mb-5 block">
              Premium Daily Wellness
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-navy-900 leading-[0.98]">
              Premium Daily Wellness, <span className="text-royal">Reframed.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-md">
              Daily support for gut health, energy, focus, vitality and immune resilience — in one
              simple sachet.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={() => navigate("/product")}
                data-testid="hero-shop-now"
                className="inline-flex items-center gap-2 bg-navy-900 text-white rounded-full px-8 py-4 font-medium hover:bg-navy-800 transition-all duration-300 shadow-lg shadow-slate-900/10 hover:-translate-y-0.5"
              >
                Shop Now <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/science"
                className="inline-flex items-center gap-2 text-navy-900 font-medium hover:text-royal transition-colors"
              >
                Discover the science <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <span className="text-sm text-slate-500">4.8/5 from 2,500+ verified reviews</span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative aspect-square w-full max-w-xl mx-auto flex items-center justify-center">
              <div className="absolute inset-0 soft-orb" />
              <BrandBadge size={140} className="absolute top-2 left-2 z-10 hidden md:block" />
              <img
                src="/images/brand/sachet_square.png"
                alt="NARA Nutrition daily wellness sachet"
                className="relative z-0 w-full h-full object-contain p-8 drop-shadow-2xl"
                data-testid="hero-product-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <SectionHeading
          align="center"
          overline="One Sachet. Multiple Systems."
          title="Everything your body needs, every day."
          subtitle="Advanced daily nutrition that works in harmony with your body — supporting what matters."
          className="mb-12"
        />
        <BenefitsRow />
      </section>

      {/* PRODUCT BADGES STRIP */}
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {PRODUCT_BADGES.map((b) => (
            <span key={b} className="text-sm font-medium tracking-wide text-slate-200 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-300" /> {b}
            </span>
          ))}
        </div>
      </section>

      {/* SCIENCE TEASER */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src="/images/gen/home_lifestyle.png" alt="Woman enjoying her daily NARA wellness ritual" className="rounded-3xl w-full object-cover shadow-xl" />
        </Reveal>
        <div>
          <SectionHeading
            overline="The Science of Daily Wellness"
            title="Rooted in science. Made for life."
            subtitle="Every formula begins with purpose and is built on clinically informed research — delivering ingredient density, bioavailability and transparency."
          />
          <div className="mt-10 grid grid-cols-2 gap-6">
            {SCIENCE_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.07}>
                <div className="flex items-start gap-3">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-sky-50 text-royal shrink-0">
                    <Icon name={s.icon} className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-heading text-2xl font-bold text-navy-900">{s.value}</p>
                    <p className="text-sm font-medium text-navy-900">{s.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Link to="/science" className="mt-9 inline-flex items-center gap-2 text-royal font-semibold hover:gap-3 transition-all">
            Explore the research <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* INGREDIENTS TEASER */}
      <section className="bg-[#FAFBFD] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <SectionHeading align="center" overline="Ingredients" title="Inside every sachet." subtitle="Premium ingredients. Purposeful blends. Proven results." className="mb-12" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
            {INGREDIENTS.map((ing, i) => (
              <Reveal key={ing.name} delay={i * 0.06}>
                <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-50 text-royal mb-4">
                    <Icon name={ing.icon} className="w-6 h-6" />
                  </span>
                  <p className="font-heading font-semibold text-navy-900 text-sm">{ing.name}</p>
                  <p className="text-xs text-slate-400 mt-2 leading-snug">{ing.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-royal to-navy-900 text-white px-8 md:px-16 py-16 md:py-20 text-center">
            <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-white/5" />
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl mx-auto leading-tight">
              Begin your daily ritual today.
            </h2>
            <p className="mt-5 text-slate-300 max-w-md mx-auto">One simple sachet. Real, lasting impact — from {formatPrice(PRODUCT.price)}.</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <button onClick={() => { addItem(PRODUCT.id, "subscribe"); }} data-testid="cta-add-to-cart" className="bg-white text-navy-900 rounded-full px-8 py-4 font-medium hover:bg-slate-100 transition-colors">
                Subscribe & Save 15%
              </button>
              <Link to="/bundles" className="border border-white/30 rounded-full px-8 py-4 font-medium hover:bg-white/10 transition-colors">
                View Bundles
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
